import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, switchMap, tap, timer} from 'rxjs';
import {MobileBridgeEvent} from '@gatewee/types/mobile';
import {RecipientUser} from '@gatewee/types/user';
import {bootstrapPublicSettings} from '@gatewee/services/core/app.settings';
import {SettingsService} from '@gatewee/services/core/settings.service';
import {BrandingService} from '@gatewee/services/core/branding.service';
import {MobileBridgeService} from '@gatewee/services/core/mobile-bridge.service';
import {
  clearAuthTokenKeys,
  clearProfileKeys,
  readAccessToken,
} from '@gatewee/services';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private bridge: MobileBridgeService,
    private settingsService: SettingsService,
    private brandingService: BrandingService,
  ) {}

  login(email: string, password: string): Observable<RecipientUser> {
    return this.http
      .post<{
        token: string;
        tokenExpiresIn: number;
        refreshToken: string;
        refreshTokenExpiresIn: number;
      }>('/auth/recipient', {email, password})
      .pipe(
        tap(({token, tokenExpiresIn, refreshToken, refreshTokenExpiresIn}) => {
          const now = Date.now();
          localStorage.setItem('accessToken', token);
          localStorage.setItem(
            'accessTokenExpiresIn',
            String(now + tokenExpiresIn * 1000),
          );
          localStorage.setItem('refreshToken', refreshToken);
          localStorage.setItem(
            'refreshTokenExpiresIn',
            String(now + refreshTokenExpiresIn * 1000),
          );

          this.bridge.send({
            type: MobileBridgeEvent.AUTH_TOKEN_UPDATE,
            payload: {
              token,
              tokenExpiresIn,
              refreshToken,
              refreshTokenExpiresIn,
            },
          });
        }),
        switchMap(() => this.profile()),
      );
  }

  refreshSettings() {
    return bootstrapPublicSettings(this.http, this.brandingService)();
  }

  profile(): Observable<RecipientUser> {
    return this.http.get<RecipientUser>('/auth/profile').pipe(
      tap(profile => {
        localStorage.setItem('profile', JSON.stringify(profile));
        this.bridge.send({
          type: MobileBridgeEvent.AUTH_USER_UPDATE,
          payload: profile,
        });
      }),
    );
  }

  logout(): void {
    clearAuthTokenKeys();
    clearProfileKeys();

    ['sessionUser'].forEach(k => {
      sessionStorage.removeItem(k);
    });

    const dropProps = [
      'paymentMethods',
      'allowLegalPersonSignup',
      'allowNaturalPersonSignup',
      'complianceRequirements',
    ];

    const trim = (store: Storage) => {
      const raw = store.getItem('app-init-settings');
      if (!raw) return;
      try {
        const obj = JSON.parse(raw);
        dropProps.forEach(p => delete obj[p]);
        store.setItem('app-init-settings', JSON.stringify(obj));
      } catch {
        // Vazio
      }
    };

    trim(sessionStorage);

    localStorage.clear();
    this.settingsService.clear();

    this.bridge.send({type: MobileBridgeEvent.AUTH_USER_LOGOUT});

    setTimeout(() => {
      window.location.href = '/login';
    }, 0);
  }

  isAccessTokenExpired(): boolean {
    const expiresAt = Number(localStorage.getItem('accessTokenExpiresIn'));
    return !expiresAt || Date.now() > expiresAt;
  }

  isRefreshTokenExpired(): boolean {
    const expiresAt = Number(localStorage.getItem('refreshTokenExpiresIn'));
    return !expiresAt || Date.now() > expiresAt;
  }

  isAuthenticated(): boolean {
    const token = readAccessToken();
    return !!token && !this.isAccessTokenExpired();
  }

  verifyInvitationToken(token: string): Observable<RecipientUser> {
    return this.http.get<RecipientUser>(`/auth/recipient/invitation/${token}`);
  }

  verifyResetToken(token: string): Observable<RecipientUser> {
    return this.http.get<RecipientUser>(
      `/auth/recipient/password-reset/${token}`,
    );
  }

  signup(user: RecipientUser): Observable<RecipientUser> {
    return this.http.post<RecipientUser>('/auth/recipient/signup', user);
  }

  passwordReset(user: RecipientUser): Observable<RecipientUser> {
    return this.http.post<RecipientUser>(
      '/auth/recipient/password-reset',
      user,
    );
  }

  passwordResetRequest(email: string): Observable<string> {
    return this.http.get<string>(
      `/auth/recipient/password-reset-request/${email}`,
    );
  }

  private refreshToken(refreshToken: string): Observable<{
    token: string;
    tokenExpiresIn: number;
    refreshToken: string;
    refreshTokenExpiresIn: number;
  }> {
    return this.http
      .post<{
        token: string;
        tokenExpiresIn: number;
        refreshToken: string;
        refreshTokenExpiresIn: number;
      }>('/auth/recipient/refresh', {refreshToken})
      .pipe(
        tap(
          (response: {
            token: string;
            tokenExpiresIn: number;
            refreshToken: string;
            refreshTokenExpiresIn: number;
          }) => {
            const {token, tokenExpiresIn, refreshToken, refreshTokenExpiresIn} =
              response;
            localStorage.setItem('accessToken', token);
            localStorage.setItem(
              'accessTokenExpiresIn',
              String(Date.now() + tokenExpiresIn * 1000),
            );
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem(
              'refreshTokenExpiresIn',
              String(Date.now() + refreshTokenExpiresIn * 1000),
            );

            this.bridge.send({
              type: MobileBridgeEvent.AUTH_TOKEN_UPDATE,
              payload: response,
            });
          },
        ),
      );
  }

  scheduleRefreshToken() {
    const expiresAt = Number(localStorage.getItem('accessTokenExpiresIn'));

    const refreshAt = expiresAt - 60 * 1000;
    const delay = Math.max(refreshAt - Date.now(), 0);

    if (!this.isAccessTokenExpired()) {
      timer(delay).subscribe(() => {
        this.executeRefreshToken().subscribe({
          next: () => console.log('Token renovado'),
          error: () => this.logout(),
        });
      });
    }
  }

  executeRefreshToken() {
    const refreshToken = localStorage.getItem('refreshToken') || '';
    return this.refreshToken(refreshToken);
  }
}
